import { memo, useEffect, useRef, useState, useMemo } from "react";
import { formatChatDate } from "@/lib/date-tools";
import { Check, Loader2 } from "lucide-react";
import { MessageRenderer } from "@/components/blocks/message-renderer";
import { VideoInfo } from "@/components/blocks/video-player";
import { getUserChatColorClass } from "@/lib/chat-colors";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

// message가 변경되지 않으면 리렌더링하지 않도록 메모이제이션
export const ChatMessage = memo(
  ({
    chat,
    index,
    allMessages,
    message,
    onPlayVideo,
    locale = "en",
  }: {
    chat: any;
    index: number;
    allMessages: any[];
    message: any;
    onPlayVideo?: (info: VideoInfo) => void;
    locale: string;
  }) => {
    const [timeString, setTimeString] = useState("");
    const elementRef = useRef(null);

    const prevMessage = useMemo(() => {
      return index > 0 ? allMessages[index - 1] : null;
    }, [allMessages, index]);
    const nextMessage = useMemo(() => {
      return index < allMessages.length - 1 ? allMessages[index + 1] : null;
    }, [allMessages, index]);
    const sender = useMemo(() => {
      return chat.userMap[message.sender._id];
    }, [chat, message]);
    const senderBgColor = useMemo(() => {
      return sender?.bgColor;
    }, [sender]);

    // 사용자별 배경색 클래스 계산
    const userBgColor = useMemo(() => {
      if (senderBgColor) {
        return "";
      }
      // if (message.isMe) {
      //   return "bg-chat text-primary-foreground";
      // }
      return getUserChatColorClass(message.sender._id);
    }, [message.isMe, message.sender._id, senderBgColor]);

    useEffect(() => {
      if (!message) {
        return;
      }

      // 이전 메시지와의 시간 차이가 1분 이내라면 시각을 표시하지 않는다.
      if (message.sender._id === prevMessage?.sender._id) {
        const diff = Math.abs(
          parseInt(prevMessage.updatedAt) - parseInt(message.updatedAt),
        );
        if (diff < 60 * 1000) {
          setTimeString("");
          return;
        }
      }

      // Intl.LocalesArgumemt로 지정한 언어로 날짜를 포맷
      const locales = locale === "ko" ? "ko-KR" : "en-US";
      // 최초 업데이트.
      setTimeString(formatChatDate(message.updatedAt, locales));
      // 화면에 보이면 1분 간격으로 업데이트.
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            // 화면에 보이는 경우에만 타이머 시작
            const timer = setInterval(() => {
              setTimeString(formatChatDate(message.updatedAt, locales));
            }, 60000); //1분마다 업데이트.

            // 화면에서 사라질 때 타이머 정리
            return () => clearInterval(timer);
          }
        },
        { threshold: 0.1 }, // 10% 이상 보이면 활성화
      );
      // ref가 가리키는 요소 관찰 시작
      if (elementRef.current) {
        observer.observe(elementRef.current);
      }
      // 컴포넌트 언마운트시 정리
      return () => {
        observer?.disconnect();
      };
    }, [message, prevMessage, locale]);

    return (
      <div className="max-w-full flex flex-col">
        <div
          className={`flex flex-col p-1 w-fit max-w-[80%] ${
            message.isMe ? "ml-auto items-end" : "mr-auto items-start"
          } ${message.isPending ? "opacity-70" : ""}`}
        >
          {!message.isMe && message.sender._id != nextMessage?.sender._id && (
            <div className="flex items-center gap-2 mb-1">
              <img
                src={sender?.picture || "/images/anonymous-icon.jpg"}
                alt={sender?.name ?? "loading"}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex flex-col">
                <div className="text-xs font-semibold">
                  {sender?.name ?? "loading"}
                </div>
                <div className="text-xs opacity-50 mt-1">
                  {/* {message.sender.email} */}
                </div>
              </div>
            </div>
          )}
          <div ref={elementRef} className="flex items-end">
            {message.isMe && (
              <div className={`flex items-center gap-1 mx-2 mb-1`}>
                {message.isPending && (
                  <Loader2 className="w-3 h-3 animate-spin" />
                )}
                <span ref={elementRef} className="text-xs opacity-50">
                  {timeString}
                </span>
              </div>
            )}
            {/* <div
              className={`w-fit p-2 text-sm break-all ${
                message.isMe
                  ? "bg-chat text-primary-foreground rounded-l-lg rounded-tr-lg text-right"
                  : "bg-chat-other rounded-r-lg rounded-tl-lg"
              }`}
            > */}
            <div
              className={`w-fit p-2 text-sm break-all text-white ${
                message.isMe
                  ? `${userBgColor} rounded-l-lg rounded-tr-lg text-right`
                  : `${userBgColor} rounded-r-lg rounded-tl-lg`
              }`}
              style={{
                ...(senderBgColor && { backgroundColor: senderBgColor }),
              }}
            >
              <MessageRenderer
                message={message.content}
                onPlayVideo={onPlayVideo}
              />
            </div>
            {!message.isMe && (
              <div className={`flex items-center gap-1 mx-1 mb-1`}>
                {message.isPending && (
                  <Loader2 className="w-3 h-3 animate-spin" />
                )}
                <span className="text-xs opacity-50">{timeString}</span>
              </div>
            )}
          </div>
        </div>
        {prevMessage &&
          chat.readMessageId &&
          chat.readMessageId === message._id && (
            <div className="relative flex items-center justify-center my-8 w-full animate-in fade-in duration-500">
              {/* 배경 라인 - shadcn Separator 활용 */}
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full bg-primary/20" />
              </div>

              {/* 중앙 배지 */}
              <div className="relative flex justify-center">
                <Badge
                  variant="secondary"
                  className="bg-background px-4 py-1.5 text-xs font-medium text-primary border-primary/30 shadow-sm flex items-center gap-1.5 rounded-full"
                >
                  <Check className="w-3.5 h-3.5" />
                  여기까지 읽었습니다
                </Badge>
              </div>
            </div>
          )}
      </div>
    );
  },
);

ChatMessage.displayName = "ChatMessage";
